import { getBasketAccess } from "./basket";

const learnerNotificationTitles = {
  section_completed: "section",
  track_section_completed: "section",
  course_section_completed: "section",
  quiz_completed: "quiz",
  certificate_earned: "certificate",
  forum_reply: "forum",
  badge_earned: "badge",
  purchase_confirmation: "purchase",
  course_purchased: "purchase",
  track_purchased: "purchase",
  digital_product_purchased: "purchase",
  enrolment_confirmation: "enrolment",
  enrollment_confirmation: "enrolment",
};

const creatorNotificationTitles = {
  template_purchase_confirmation: "templatePurchase",
  template_purchased: "templatePurchase",
  personalise_purchase_confirmation: "personalisePurchase",
  personalise_purchased: "personalisePurchase",
  product_sold: "productSold",
  course_sold: "productSold",
  track_sold: "productSold",
  digital_product_sold: "productSold",
  product_published: "productPublished",
  course_published: "productPublished",
  track_published: "productPublished",
  digital_product_published: "productPublished",
  admin_review_approved: "review",
  admin_review_rejected: "review",
  storage_threshold: "storage",
  storage_75: "storage",
  storage_warning: "storage",
};

export const normalizeCreatorNotification = (item) => ({
  ...item,
  source: "creator",
  _uniqueId: `creator-${item._id}`,
  titleKey: `notificationTitles.${
    creatorNotificationTitles[
      String(item.type || item.event_type || "")
        .toLowerCase()
        .replace(/[\s-]/g, "_")
    ] || "teachingGeneral"
  }`,
  message: item.text || item.message,
  viewed: item.read,
  createdAt:
    item.createdAt ||
    (item.date ? `${item.date}T${item.time || "00:00"}` : undefined),
});

export const normalizeLearnerNotification = (item) => ({
  ...item,
  source: "learner",
  _uniqueId: `learner-${item._id}`,
  titleKey: `notificationTitles.${
    learnerNotificationTitles[
      String(item.type || item.event_type || "")
        .toLowerCase()
        .replace(/[\s-]/g, "_")
    ] || "general"
  }`,
  message: item.text || item.message,
  viewed: item.read,
  createdAt:
    item.createdAt ||
    (item.date ? `${item.date}T${item.time || "00:00"}` : undefined),
});

const learningEvents = new Set([
  "section_completed",
  "track_section_completed",
  "course_section_completed",
  "quiz_completed",
  "certificate_earned",
  "forum_reply",
  "badge_earned",
  "purchase_confirmation",
  "enrolment_confirmation",
  "enrollment_confirmation",
  "course_purchased",
  "track_purchased",
  "digital_product_purchased",
]);
const teachingEvents = new Set([
  "template_purchase_confirmation",
  "personalise_purchase_confirmation",
  "template_purchased",
  "personalise_purchased",
  "product_sold",
  "course_sold",
  "track_sold",
  "digital_product_sold",
  "product_published",
  "course_published",
  "track_published",
  "digital_product_published",
  "admin_review_approved",
  "admin_review_rejected",
  "storage_threshold",
  "storage_75",
  "storage_warning",
]);

export const isAccountNotificationEvent = (item) => {
  const event = String(item.event_type || item.type || "")
    .toLowerCase()
    .replace(/[\s-]/g, "_");
  return (
    learningEvents.has(event) ||
    teachingEvents.has(event) ||
    Boolean(item.event_type)
  );
};

export const getNotificationActivity = (item) => {
  const event = String(item.event_type || item.type || "")
    .toLowerCase()
    .replace(/[\s-]/g, "_");
  if (
    item.isCampaign ||
    item.campaignid ||
    item.isReportedPost ||
    item.reportaction_id ||
    ["feed", "social"].includes(item.channel) ||
    /^(feed_|social_|post_|follow|like)/.test(event)
  )
    return null;
  if (item.source === "learner") return "learning";
  if (item.source === "creator") return "teaching";
  if (
    event === "forum_reply" &&
    (item.enrolled === false ||
      (!item.course_id &&
        !item.courseid &&
        !item.course &&
        !item.enrolled_content_id))
  )
    return null;
  if (
    [
      "product_sold",
      "product_published",
      "admin_review_approved",
      "admin_review_rejected",
    ].includes(event) &&
    ["template", "personalise"].includes(item.product_type)
  )
    return null;
  if (
    event.startsWith("storage_") &&
    item.storage_percentage != null &&
    Number(item.storage_percentage) < 75
  )
    return null;
  if (learningEvents.has(event)) return "learning";
  if (teachingEvents.has(event)) return "teaching";
  if (item.activity === "learning" || item.activity === "teaching")
    return item.activity;
  if (
    item.isExpiry ||
    item.expiry_date ||
    item.isTemplateUpdate ||
    item.notification_id
  )
    return "teaching";
  if (["approved", "rejected", "published"].includes(item.status))
    return "teaching";
  return item.source === "course-status" ? "teaching" : null;
};

export const getAccountNotifications = (items, role, unreadOnly = false) => {
  const access = getBasketAccess(role);
  const seen = new Set();
  return items
    .map((item) => ({ ...item, activity: getNotificationActivity(item) }))
    .filter((item) => {
      if (!item.activity || !access[item.activity]) return false;
      if (unreadOnly && (item.read || item.viewed || item.expiry_action))
        return false;
      const key =
        item._uniqueId ||
        `${item.source || "notification"}:${item.notification_id || item._id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort(
      (first, second) =>
        (Date.parse(second.createdAt) || 0) -
        (Date.parse(first.createdAt) || 0),
    );
};
